// =============================================
// LoginPage.jsx - 로그인 페이지
// =============================================
// 두 가지 로그인 방식을 나란히 보여줘:
//   1. 구글 로그인 (Google Sign-In 버튼)
//   2. 텔레그램 로그인 (Telegram Login Widget)
//
// ★ 로그인 흐름:
//   ① 사용자가 버튼 클릭
//   ② 각 서비스에서 인증 완료
//   ③ 인증 데이터를 백엔드(POST /api/auth/google 또는 /telegram)로 전송
//   ④ 백엔드가 JWT 토큰 반환
//   ⑤ AuthContext.login(token) 호출 → 전역 상태 업데이트
//   ⑥ /portfolio 페이지로 자동 이동
//
// ★ 환경변수 설정 필요:
//   frontend/.env 파일에 아래 두 줄을 추가해!
//   REACT_APP_GOOGLE_CLIENT_ID=xxx.apps.googleusercontent.com
//   REACT_APP_TELEGRAM_BOT_USERNAME=your_bot_username
// =============================================

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = 'http://localhost:8000';

// ─── 환경변수 읽기 ───────────────────────────
// REACT_APP_ 접두사를 붙여야 React가 .env에서 읽어와.
const GOOGLE_CLIENT_ID      = process.env.REACT_APP_GOOGLE_CLIENT_ID      || '';
const TELEGRAM_BOT_USERNAME = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || '';


// =============================================
// LoginPage 컴포넌트
// =============================================
export default function LoginPage() {
  const { login, isLoggedIn } = useAuth();
  const navigate              = useNavigate();
  const telegramDivRef        = useRef(null);  // 텔레그램 위젯이 렌더링될 div

  const [error,   setError]   = useState('');   // 에러 메시지
  const [loading, setLoading] = useState('');   // 로딩 중인 버튼 ('google' | 'telegram' | '')

  // ─── 이미 로그인됐으면 홈으로 이동 ───────
  useEffect(() => {
    if (isLoggedIn) navigate('/', { replace: true });
  }, [isLoggedIn, navigate]);


  // =============================================
  // ① 구글 GSI 스크립트 로드
  // =============================================
  // Google Identity Services(GIS) 스크립트를 동적으로 로드해.
  // 스크립트가 로드된 후 google.accounts.id.initialize()를 호출해서
  // "Google로 로그인" 버튼을 렌더링해.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;  // 환경변수 없으면 스킵

    // ─ 전역 콜백 함수 등록 ─
    // 구글 버튼을 클릭하고 인증이 완료되면 이 함수가 호출돼.
    // response.credential: 구글 ID 토큰 (JWT 형태)
    window.__googleCredentialCallback = async (response) => {
      await handleGoogleLogin(response.credential);
    };

    // ─ 스크립트 태그 생성 ─
    const script = document.createElement('script');
    script.src   = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      // 스크립트 로드 완료 → 구글 버튼 초기화
      if (!window.google?.accounts?.id) return;

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback:  window.__googleCredentialCallback,
      });

      // ID "google-signin-btn" div에 구글 공식 버튼 렌더링
      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-btn'),
        {
          theme: 'filled_black',   // 다크 테마
          size:  'large',
          width: 280,
          text:  'signin_with',    // "Google로 로그인" 텍스트
        }
      );
    };

    document.head.appendChild(script);

    // 컴포넌트 언마운트 시 정리
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      delete window.__googleCredentialCallback;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // =============================================
  // ② 텔레그램 Login Widget 스크립트 로드
  // =============================================
  // 텔레그램 Login Widget은 스크립트 태그로 동작해.
  // data-onauth 속성에 지정한 전역 함수가 인증 성공 시 호출돼.
  useEffect(() => {
    if (!TELEGRAM_BOT_USERNAME || !telegramDivRef.current) return;

    // ─ 전역 콜백 함수 등록 ─
    // 텔레그램 버튼 클릭 + 인증 완료 시 이 함수가 호출돼.
    // user: { id, first_name, last_name, username, photo_url, auth_date, hash }
    window.onTelegramAuth = async (user) => {
      await handleTelegramLogin(user);
    };

    // ─ 스크립트 태그 생성 ─
    const script = document.createElement('script');
    script.src   = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', TELEGRAM_BOT_USERNAME);
    script.setAttribute('data-size',           'large');
    script.setAttribute('data-onauth',         'onTelegramAuth');
    script.setAttribute('data-request-access', 'write');
    script.async = true;

    // telegramDivRef.current에 스크립트를 주입 → 텔레그램 버튼 렌더링
    if (telegramDivRef.current) {
      telegramDivRef.current.appendChild(script);
    }

    return () => {
      delete window.onTelegramAuth;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // =============================================
  // 구글 로그인 처리
  // =============================================
  // credential: 구글 GSI가 반환하는 ID 토큰 (JWT)
  // → 백엔드 POST /api/auth/google 로 전송
  const handleGoogleLogin = async (credential) => {
    setLoading('google');
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/google`, { credential });
      // 성공 → AuthContext에 토큰 저장
      login(res.data.token);
      navigate('/portfolio');  // 내 자산 페이지로 이동
    } catch (e) {
      const msg = e.response?.data?.detail || '구글 로그인 중 오류가 발생했어.';
      setError(msg);
    } finally {
      setLoading('');
    }
  };


  // =============================================
  // 텔레그램 로그인 처리
  // =============================================
  // user: 텔레그램 위젯이 전달하는 인증 데이터
  //   { id, first_name, last_name, username, photo_url, auth_date, hash }
  // → 백엔드 POST /api/auth/telegram 로 전송
  const handleTelegramLogin = async (user) => {
    setLoading('telegram');
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/telegram`, user);
      login(res.data.token);
      navigate('/portfolio');
    } catch (e) {
      const msg = e.response?.data?.detail || '텔레그램 로그인 중 오류가 발생했어.';
      setError(msg);
    } finally {
      setLoading('');
    }
  };


  // =============================================
  // 렌더링
  // =============================================
  return (
    <div style={{
      minHeight:      '70vh',
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      gap:            '1.5rem',
      padding:        '2rem',
    }}>

      {/* ─── 로고 + 타이틀 ─── */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>💹</div>
        <h1 style={{ color: '#e2e8f0', margin: 0, fontSize: '1.8rem' }}>돈텔미</h1>
        <p style={{ color: '#718096', marginTop: '0.5rem', fontSize: '0.95rem' }}>
          로그인하고 내 자산을 관리해봐
        </p>
      </div>

      {/* ─── 로그인 카드 ─── */}
      <div style={{
        backgroundColor: '#1a202c',
        borderRadius:    '16px',
        padding:         '2rem',
        width:           '100%',
        maxWidth:        '380px',
        display:         'flex',
        flexDirection:   'column',
        gap:             '1rem',
        boxShadow:       '0 4px 24px rgba(0,0,0,0.4)',
      }}>

        <h2 style={{
          color:      '#e2e8f0',
          margin:     0,
          fontSize:   '1.1rem',
          textAlign:  'center',
          fontWeight: 600,
        }}>
          로그인 방법 선택
        </h2>

        {/* ─── 에러 메시지 ─── */}
        {error && (
          <div style={{
            backgroundColor: '#742a2a',
            color:           '#feb2b2',
            padding:         '0.75rem 1rem',
            borderRadius:    '8px',
            fontSize:        '0.85rem',
            textAlign:       'center',
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* ─── 구글 로그인 버튼 ─── */}
        <div>
          <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.5rem' }}>
            구글 계정으로 로그인
          </div>
          {GOOGLE_CLIENT_ID ? (
            // ★ google.accounts.id.renderButton()이 이 div 안에 버튼을 그려줘
            <div
              id="google-signin-btn"
              style={{
                display:        'flex',
                justifyContent: 'center',
                minHeight:      '44px',
                opacity:        loading === 'telegram' ? 0.5 : 1,
              }}
            />
          ) : (
            <div style={{
              padding:         '0.75rem',
              backgroundColor: '#2d3748',
              borderRadius:    '8px',
              color:           '#718096',
              fontSize:        '0.82rem',
              textAlign:       'center',
            }}>
              ⚙️ REACT_APP_GOOGLE_CLIENT_ID 환경변수 미설정
            </div>
          )}
        </div>

        {/* ─── 구분선 ─── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#2d3748' }} />
          <span style={{ color: '#4a5568', fontSize: '0.78rem' }}>또는</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#2d3748' }} />
        </div>

        {/* ─── 텔레그램 로그인 위젯 ─── */}
        <div>
          <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.5rem' }}>
            텔레그램 계정으로 로그인
          </div>
          {TELEGRAM_BOT_USERNAME ? (
            // ★ useEffect에서 텔레그램 위젯 스크립트가 이 div에 버튼을 삽입해줘
            <div
              ref={telegramDivRef}
              style={{
                display:        'flex',
                justifyContent: 'center',
                minHeight:      '44px',
                opacity:        loading === 'google' ? 0.5 : 1,
              }}
            />
          ) : (
            <div style={{
              padding:         '0.75rem',
              backgroundColor: '#2d3748',
              borderRadius:    '8px',
              color:           '#718096',
              fontSize:        '0.82rem',
              textAlign:       'center',
            }}>
              ⚙️ REACT_APP_TELEGRAM_BOT_USERNAME 환경변수 미설정
            </div>
          )}
        </div>

        {/* ─── ★★★ 텔레그램 봇 연동 경고 박스 ─── */}
        {/* 이 경고는 매우 중요해! 눈에 잘 띄도록 붉은 박스로 강조해. */}
        <div style={{
          backgroundColor: '#742a2a',  // 짙은 빨간색 배경
          border:          '1px solid #fc8181',
          borderRadius:    '10px',
          padding:         '1rem',
          marginTop:       '0.5rem',
        }}>
          <div style={{
            display:        'flex',
            alignItems:     'center',
            gap:            '0.5rem',
            marginBottom:   '0.5rem',
            fontWeight:     700,
            color:          '#fc8181',
            fontSize:       '0.9rem',
          }}>
            ⚠️ 텔레그램 봇 연동 사용자 필독!
          </div>
          <p style={{
            color:      '#fed7d7',
            fontSize:   '0.82rem',
            margin:     0,
            lineHeight: 1.6,
          }}>
            <strong>텔레그램 봇 알림</strong>을 받고 싶다면<br />
            반드시 <strong style={{ color: '#fc8181' }}>텔레그램으로 로그인</strong>해야 해!<br />
            <br />
            구글로 로그인하면 텔레그램 계정과 연동이 되지 않아서<br />
            자산 알림을 받을 수 없어.
          </p>
        </div>

      </div>

      {/* ─── 하단 안내 ─── */}
      <p style={{ color: '#4a5568', fontSize: '0.78rem', textAlign: 'center', maxWidth: '320px' }}>
        로그인하면 내 자산 데이터가 계정에 안전하게 저장돼.<br />
        처음 로그인 시 기존에 추가한 자산이 자동으로 연결돼.
      </p>

    </div>
  );
}
